/**
 * Search Page
 *
 * Search for songs, albums, artists, playlists.
 * Responsive grid layout for results.
 */

import { useState, useCallback, useEffect } from 'react';
import { api } from '@/api/client';
import { SearchIcon } from '@/components/icons';
import { MediaCard } from '@/components/common/MediaCard';
import { LoadingGrid } from '@/components/common/LoadingGrid';

type FilterType = 'all' | 'songs' | 'albums' | 'artists' | 'playlists';

interface SearchResults {
  songs: unknown[];
  albums: unknown[];
  artists: unknown[];
  playlists: unknown[];
}

export function SearchPage() {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Debounced search
  const search = useCallback(async (q: string) => {
    if (!q.trim()) {
      setResults(null);
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.search(q, filter === 'all' ? undefined : filter);
      if (response.data) {
        setResults(response.data);
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filter]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      search(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, search]);

  const filters: { value: FilterType; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'songs', label: 'Songs' },
    { value: 'albums', label: 'Albums' },
    { value: 'artists', label: 'Artists' },
    { value: 'playlists', label: 'Playlists' },
  ];

  return (
    <div className="min-h-full">
      {/* Search header - sticky */}
      <header className="sticky top-0 z-20 bg-black/90 backdrop-blur-lg px-4 py-4 space-y-4">
        {/* Search input */}
        <div className="relative">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-on-surface-variant" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search songs, albums, artists..."
            className="input pl-12 pr-4"
            autoFocus
          />
        </div>

        {/* Filter chips */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
          {filters.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors
                ${filter === f.value
                  ? 'bg-primary text-on-primary'
                  : 'bg-surface-container-high text-on-surface hover:bg-surface-container-highest'
                }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </header>

      {/* Results */}
      <div className="px-4 pb-8">
        {isLoading ? (
          <LoadingGrid />
        ) : !results ? (
          <div className="text-center py-12">
            <p className="text-on-surface-variant">
              Search for your favorite music
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Songs */}
            {(filter === 'all' || filter === 'songs') && results.songs.length > 0 && (
              <section>
                <h2 className="text-lg font-bold mb-4">Songs</h2>
                <div className="space-y-2">
                  {results.songs.slice(0, filter === 'all' ? 5 : 50).map((song: any, index) => (
                    <MediaCard key={song.id || index} item={song} variant="list" />
                  ))}
                </div>
              </section>
            )}

            {/* Albums */}
            {(filter === 'all' || filter === 'albums') && results.albums.length > 0 && (
              <section>
                <h2 className="text-lg font-bold mb-4">Albums</h2>
                <div className="media-grid">
                  {results.albums.slice(0, filter === 'all' ? 6 : 50).map((album: any, index) => (
                    <MediaCard key={album.id || index} item={album} variant="grid" />
                  ))}
                </div>
              </section>
            )}

            {/* Artists */}
            {(filter === 'all' || filter === 'artists') && results.artists.length > 0 && (
              <section>
                <h2 className="text-lg font-bold mb-4">Artists</h2>
                <div className="media-grid">
                  {results.artists.slice(0, filter === 'all' ? 6 : 50).map((artist: any, index) => (
                    <MediaCard key={artist.id || index} item={artist} variant="grid" isArtist />
                  ))}
                </div>
              </section>
            )}

            {/* Playlists */}
            {(filter === 'all' || filter === 'playlists') && results.playlists.length > 0 && (
              <section>
                <h2 className="text-lg font-bold mb-4">Playlists</h2>
                <div className="media-grid">
                  {results.playlists.slice(0, filter === 'all' ? 6 : 50).map((playlist: any, index) => (
                    <MediaCard key={playlist.id || index} item={playlist} variant="grid" />
                  ))}
                </div>
              </section>
            )}

            {/* No results */}
            {results.songs.length === 0 &&
              results.albums.length === 0 &&
              results.artists.length === 0 &&
              results.playlists.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-on-surface-variant">
                    No results found for "{query}"
                  </p>
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );
}
